import { useMemo, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import Chart from "@/components/Chart";
import ChartLegend from "@/components/Chart/ChartLegend";
import ChartTooltip from "@/components/Chart/ChartTooltip";
import { sysColors } from "@/assets/theme/colors";
import { formatCount, formatPercent, percentOf } from "../_utils";
import type { PatResultVolumeAttemptBucket, PatResultVolumeYear } from "../queries";

const PASS_COLOR = sysColors["sys-brand-primary"];
const FAIL_COLOR = sysColors["sys-brand-error"] || sysColors["sys-brand-tertiary"];
const TOTAL_COLOR = sysColors["sys-brand-outline"];

type Props = {
  years: PatResultVolumeYear[];
  passedByAttempt: PatResultVolumeAttemptBucket[];
  loading?: boolean;
};

function ChartCard({ title, testId, children }: { title: string; testId: string; children: ReactNode }) {
  return (
    <div
      className="flex flex-col gap-2 border border-sys-brand-outline rounded-lg p-4 h-full min-w-0"
      data-testid={testId}
    >
      <p className="text-title-medium text-sys-brand-on-surface m-0">{title}</p>
      <div className="w-full min-w-0">{children}</div>
    </div>
  );
}

function EmptyChartMessage({ label }: { label: string }) {
  return (
    <div className="flex h-[225px] items-center justify-center">
      <p className="text-body-small text-sys-brand-on-surface-variant m-0 text-center">{label}</p>
    </div>
  );
}

export default function PatVolumeCharts({ years, passedByAttempt, loading = false }: Props) {
  const chronologicalYears = useMemo(() => [...years].sort((a, b) => a.year - b.year), [years]);

  const passFailMix = useMemo(() => {
    const passed = years.reduce((sum, row) => sum + row.passedCount, 0);
    const failed = years.reduce((sum, row) => sum + row.lookerFailCount, 0);
    return [
      { name: "Pass", value: passed },
      { name: "Fail", value: failed }
    ];
  }, [years]);

  const percentByYear = useMemo(
    () =>
      chronologicalYears.map((row) => ({
        name: String(row.year),
        Pass: Number(percentOf(row.passedCount, row.scoredCount).toFixed(2)),
        Fail: Number(percentOf(row.lookerFailCount, row.scoredCount).toFixed(2))
      })),
    [chronologicalYears]
  );

  const countsByYear = useMemo(
    () =>
      chronologicalYears.map((row) => ({
        name: String(row.year),
        Pass: row.passedCount,
        Fail: row.lookerFailCount,
        Total: row.scoredCount
      })),
    [chronologicalYears]
  );

  const attemptChartData = useMemo(
    () => passedByAttempt.map((bucket) => ({ name: bucket.label, count: bucket.passedPeopleCount })),
    [passedByAttempt]
  );

  const mixTotal = passFailMix.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="grid gap-4 md:grid-cols-2 mt-8">
      <ChartCard title="Pass / Fail percentage" testId="pat-results-pass-fail-chart">
        {loading ? (
          <EmptyChartMessage label="Loading PAT Volume" />
        ) : mixTotal === 0 ? (
          <EmptyChartMessage label="No Scored PAT Attempts in This Range" />
        ) : (
          <>
            <p className="text-body-medium text-sys-brand-on-surface-variant m-0">
              {formatCount(mixTotal)} scored attempts · Pass {formatPercent(percentOf(passFailMix[0].value, mixTotal))}{" "}
              · Fail {formatPercent(percentOf(passFailMix[1].value, mixTotal))}
            </p>
            <ResponsiveContainer width="100%" height={225}>
              <PieChart>
                <Pie data={passFailMix} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90}>
                  {passFailMix.map((entry) => (
                    <Cell key={entry.name} fill={entry.name === "Pass" ? PASS_COLOR : FAIL_COLOR} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active = false, payload }) => {
                    const items =
                      payload?.map((entry) => ({
                        name: String(entry.name ?? ""),
                        value: Number(entry.value ?? 0),
                        color: String(entry.payload?.fill ?? entry.color ?? "")
                      })) ?? [];
                    return <ChartTooltip active={active} title="Pass / Fail" items={items} />;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <ChartLegend
              items={[
                { name: "Pass", color: PASS_COLOR, visible: true },
                { name: "Fail (Failed + Qualified)", color: FAIL_COLOR, visible: true }
              ]}
            />
          </>
        )}
      </ChartCard>

      <Chart
        variant="line"
        title="Percent pass / fail by year"
        subtitle="Share of scored attempts. Fail is Failed + Qualified."
        loading={loading}
        data={percentByYear}
        lines={[
          { dataKey: "Pass", name: "Pass", color: PASS_COLOR },
          { dataKey: "Fail", name: "Fail", color: FAIL_COLOR }
        ]}
      />

      <ChartCard title="Pass / Fail by year" testId="pat-results-counts-by-year-chart">
        {loading || chronologicalYears.length === 0 ? (
          <EmptyChartMessage label={loading ? "Loading PAT Volume" : "No Scored PAT Attempts in This Range"} />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={225}>
              <BarChart data={countsByYear} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="0 0" stroke={sysColors["sys-brand-surface-container-high"]} />
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  stroke={sysColors["sys-brand-surface-container-high"]}
                  tick={{ fontSize: 12, fill: sysColors["sys-brand-on-surface-variant"] }}
                />
                <YAxis
                  tickLine={false}
                  stroke={sysColors["sys-brand-surface-container-high"]}
                  tick={{ fontSize: 12, fill: sysColors["sys-brand-on-surface-variant"] }}
                  allowDecimals={false}
                />
                <Tooltip
                  content={({ active = false, payload, label }) => {
                    const items =
                      payload?.map((entry) => ({
                        name: String(entry.name ?? ""),
                        value: Number(entry.value ?? 0),
                        color: String(entry.color ?? PASS_COLOR)
                      })) ?? [];
                    return (
                      <ChartTooltip
                        active={active}
                        title="Pass / Fail by year"
                        subtitle={String(label)}
                        items={items}
                      />
                    );
                  }}
                />
                <Bar dataKey="Pass" name="Pass" fill={PASS_COLOR} />
                <Bar dataKey="Fail" name="Fail" fill={FAIL_COLOR} />
                <Bar dataKey="Total" name="Total" fill={TOTAL_COLOR} />
              </BarChart>
            </ResponsiveContainer>
            <ChartLegend
              items={[
                { name: "Pass", color: PASS_COLOR, visible: true },
                { name: "Fail", color: FAIL_COLOR, visible: true },
                { name: "Total", color: TOTAL_COLOR, visible: true }
              ]}
            />
          </>
        )}
      </ChartCard>

      <Chart
        variant="line"
        title="PATs passed by number of attempts"
        subtitle="People whose first pass is in this year range, by scored attempts through that pass."
        loading={loading}
        data={attemptChartData}
        lines={[{ dataKey: "count", name: "People passed", color: PASS_COLOR }]}
      />
    </div>
  );
}
